import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { Button } from '@/components/ui/Button';
import {
  getMarketingProvider,
  marketingRedirectUri,
  type MarketingOAuthProvider,
} from '@/features/integrations/marketingOAuth';

/**
 * Shared callback page for the six marketing OAuth providers. Each
 * provider's route (e.g. `/integrations/meta-ads/callback`) mounts this
 * with a different `provider` prop. We pick the matching
 * `<provider>-oauth-callback` Edge Function based on the prop, so the
 * routing layer doesn't need a copy-paste per provider.
 *
 * Flow:
 *   1. Validate `state` against sessionStorage (CSRF defence, same as
 *      every other Atlas callback page).
 *   2. POST `code + redirect_uri` to `<provider>-oauth-callback`.
 *   3. The Edge Function exchanges the code, provisions ad / social
 *      account rows, fires an initial sync, and returns the discovered
 *      account list.
 *   4. Show a success toast naming the accounts and navigate back to
 *      `/integrations`.
 */
export interface MarketingCallbackPageProps {
  provider: MarketingOAuthProvider;
}

interface OAuthCallbackResponse {
  ok: boolean;
  // Returned by amazon-ads / google-ads / meta-ads / facebook /
  // instagram OAuth callbacks.
  accounts?: Array<{ accountId: string; name: string }>;
  // TikTok returns both kinds of accounts in one response.
  ad_accounts?: Array<{ accountId: string; name: string }>;
  social_accounts?: Array<{ accountId: string; name: string }>;
  error?: string;
}

export function MarketingCallbackPage({ provider }: MarketingCallbackPageProps) {
  const config = getMarketingProvider(provider);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode mounts effects twice in dev; the OAuth `code`
  // is single-use, so guard against the second invocation eating it.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setError(`${config.label} rejected the connection: ${oauthError}`);
      return;
    }
    if (!code || !state) {
      setError(`Missing OAuth code or state from ${config.label}.`);
      return;
    }

    const expectedState = sessionStorage.getItem(config.stateStorageKey);
    if (!expectedState || state !== expectedState) {
      setError(
        'OAuth state mismatch. For your security, retry the connection from the Integrations page.',
      );
      return;
    }
    sessionStorage.removeItem(config.stateStorageKey);

    const fnName = `${provider.replace('_', '-')}-oauth-callback`;
    const redirectUri = marketingRedirectUri(provider);

    (async () => {
      const { data, error: fnError } = await supabase.functions.invoke<OAuthCallbackResponse>(
        fnName,
        { body: { code, state, redirect_uri: redirectUri } },
      );
      if (fnError) {
        const msg = await extractFunctionErrorMessage(fnError);
        setError(msg);
        return;
      }
      if (data && data.ok === false && data.error) {
        setError(data.error);
        return;
      }

      const all = [
        ...(data?.accounts ?? []),
        ...(data?.ad_accounts ?? []),
        ...(data?.social_accounts ?? []),
      ];
      const summary =
        all.length === 0
          ? `${config.label} connected.`
          : all.length === 1
          ? `Connected ${all[0].name}.`
          : `Connected ${all.length} ${config.label} accounts.`;
      toast.success(`${summary} Pulling data…`);
      navigate('/integrations', { replace: true });
    })();
  }, [params, navigate, provider, config.label, config.stateStorageKey]);

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-4 text-center max-w-lg">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" onClick={() => navigate('/integrations', { replace: true })}>
            Back to Integrations
          </Button>
        </div>
      </div>
    );
  }
  return <LoadingScreen />;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
